import React, { useState, useEffect } from "react";
import {
  Activity,
  Users,
  Award,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  RefreshCw,
  Download,
  Search,
  Calendar,
  TrendingUp,
  UserPlus,
  FileText,
  Settings,
  Loader,
  ArrowLeft,
  Eye,
  XCircle,
} from "lucide-react";
import { apiService } from "../../services/api";
import "./ActivityMonitor.css";

const ActivityMonitor = () => {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedTimeframe, setSelectedTimeframe] = useState("today");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    userActions: 0,
    assessments: 0,
    systemEvents: 0,
  });

  // Activity type configuration
  const activityTypes = {
    user_signup: { icon: UserPlus, color: "#10b981", label: "User Signup" },
    user_login: { icon: Users, color: "#2563eb", label: "User Login" },
    assessment_started: {
      icon: BookOpen,
      color: "#7c3aed",
      label: "Assessment Started",
    },
    assessment_completed: {
      icon: Award,
      color: "#ea580c",
      label: "Assessment Completed",
    },
    assessment_created: {
      icon: FileText,
      color: "#10b981",
      label: "Assessment Created",
    },
    assessment_updated: {
      icon: Settings,
      color: "#f59e0b",
      label: "Assessment Updated",
    },
    system_alert: {
      icon: AlertCircle,
      color: "#ef4444",
      label: "System Alert",
    },
    system_status: {
      icon: CheckCircle,
      color: "#10b981",
      label: "System Status",
    },
    content_update: {
      icon: TrendingUp,
      color: "#7c3aed",
      label: "Content Update",
    },
  };

  // Fetch activities
  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeframe]);

  // Auto-refresh
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchActivities(true);
      }, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedTimeframe]);

  // Filter activities
  useEffect(() => {
    let filtered = activities;

    // Filter by type
    if (selectedType !== "all") {
      filtered = filtered.filter((activity) => activity.type === selectedType);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((activity) =>
        activity.message.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredActivities(filtered);

    // Update stats
    setStats({
      total: filtered.length,
      userActions: filtered.filter((a) =>
        ["user_signup", "user_login"].includes(a.type),
      ).length,
      assessments: filtered.filter((a) => a.type.includes("assessment")).length,
      systemEvents: filtered.filter((a) => a.type.includes("system")).length,
    });
  }, [activities, selectedType, searchTerm]);

  const fetchActivities = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const limit =
        selectedTimeframe === "today"
          ? 50
          : selectedTimeframe === "week"
            ? 100
            : 200;

      const response = await apiService.admin.getRecentActivity(limit);

      if (response?.success) {
        const activitiesWithIcons = response.activities.map((activity) => ({
          ...activity,
          iconComponent: activityTypes[activity.type]?.icon || Activity,
          color: activityTypes[activity.type]?.color || "#6b7280",
          label: activityTypes[activity.type]?.label || activity.type,
        }));
        setActivities(activitiesWithIcons);
      } else {
        // Fallback data
        setActivities([
          {
            id: 1,
            type: "system_status",
            message: "System running smoothly",
            timestamp: "Now",
            iconComponent: CheckCircle,
            color: "#10b981",
            label: "System Status",
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ["Timestamp", "Type", "Message"],
      ...filteredActivities.map((activity) => [
        activity.timestamp,
        activity.label,
        activity.message,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-log-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="activity-monitor">
      {/* Header */}
      <div className="monitor-header">
        <div className="header-content">
          <button
            className="back-button"
            onClick={() => (window.location.href = "/admin/dashboard")}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1>
              <Activity size={28} />
              Activity Monitor
            </h1>
            <p>Real-time platform activity tracking and monitoring</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className={`btn ${autoRefresh ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw size={18} className={autoRefresh ? "spinning" : ""} />
            {autoRefresh ? "Auto-Refresh On" : "Auto-Refresh Off"}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fetchActivities()}
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={handleExport}>
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="monitor-stats">
        <div className="stat-card blue">
          <div className="stat-icon">
            <Activity size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Activities</div>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.userActions}</div>
            <div className="stat-label">User Actions</div>
          </div>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon">
            <Award size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.assessments}</div>
            <div className="stat-label">Assessments</div>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.systemEvents}</div>
            <div className="stat-label">System Events</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="monitor-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <XCircle size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="user_signup">User Signups</option>
            <option value="user_login">User Logins</option>
            <option value="assessment_started">Assessments Started</option>
            <option value="assessment_completed">Assessments Completed</option>
            <option value="assessment_created">Assessments Created</option>
            <option value="system_alert">System Alerts</option>
            <option value="system_status">System Status</option>
          </select>
        </div>

        <div className="filter-group">
          <Calendar size={18} />
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>

      {/* Activity List */}
      <div className="monitor-content">
        {loading ? (
          <div className="loading-state">
            <Loader size={48} className="spinner" />
            <p>Loading activities...</p>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="empty-state">
            <Eye size={64} />
            <h3>No Activities Found</h3>
            <p>
              {searchTerm || selectedType !== "all"
                ? "Try adjusting your filters"
                : "No activities recorded yet"}
            </p>
          </div>
        ) : (
          <div className="activity-timeline">
            {filteredActivities.map((activity) => {
              const IconComponent = activity.iconComponent;
              return (
                <div key={activity.id} className="timeline-item">
                  <div className="timeline-marker">
                    <div
                      className="marker-icon"
                      style={{
                        backgroundColor: `${activity.color}20`,
                        color: activity.color,
                      }}
                    >
                      <IconComponent size={20} />
                    </div>
                    <div className="marker-line"></div>
                  </div>
                  <div className="timeline-content">
                    <div className="activity-header">
                      <span
                        className="activity-type"
                        style={{
                          backgroundColor: `${activity.color}15`,
                          color: activity.color,
                        }}
                      >
                        {activity.label}
                      </span>
                      <span className="activity-time">
                        <Clock size={14} />
                        {activity.timestamp}
                      </span>
                    </div>
                    <p className="activity-message">{activity.message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityMonitor;
